import { List, ListItem, ListItemText, Typography } from '@mui/material';
import React from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';

import Modal from '../../../components/general/modal';

export interface DuplicateRequestService {
    serviceInvestigationId: number;
    /** The service as the doctor picked it in the form, so the list is recognisable. */
    name: string;
    /** ISO timestamp of the newest still-live request for this service. */
    lastRequestedAt: string;
}

export interface DuplicateRequestModalProps extends LocalizeContextProps {
    open: boolean;
    closeModal: () => void;
    onConfirm: () => void;
    loading: boolean;
    duplicates: DuplicateRequestService[];
}

/**
 * SHER-575 — confirmation shown when the patient already has a live request for a
 * laboratory test the doctor is ordering again.
 *
 * It is a confirmation, not a gate: the API no longer refuses the repeat on tenants
 * with the flag on, so cancelling here is the doctor changing their mind, not the
 * system saying no. Each repeated line is listed with the date of the request it
 * would duplicate — "already requested recently" with no date is not something a
 * doctor can act on, and the dates are what tell a legitimate morning-after control
 * apart from a double-click.
 */
export const DuplicateRequestModal: React.FC<DuplicateRequestModalProps> = ({ open, closeModal, onConfirm, loading, duplicates, activeLanguage }) => {
    const formatDate = (isoDate: string) => {
        const date = new Date(isoDate);
        if (Number.isNaN(date.getTime())) {
            return isoDate;
        }
        return date.toLocaleString(activeLanguage ? activeLanguage.code : undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    };

    return (
        <Modal
            open={open}
            closeModal={closeModal}
            confirmAction={loading ? null : onConfirm}
            disabled={loading}
            fullWidth
            maxWidth="sm"
            title={<Translate id="pages.hospital.services.request.duplicate.title" />}
        >
            <div style={{ width: '100%' }} data-testid="duplicate-request-modal">
                <Typography gutterBottom>
                    <Translate id="pages.hospital.services.request.duplicate.modal" />
                </Typography>
                <List dense>
                    {duplicates.map((duplicate) => (
                        <ListItem key={duplicate.serviceInvestigationId} disableGutters>
                            <ListItemText
                                primary={duplicate.name}
                                secondary={<Translate id="pages.hospital.services.request.duplicate.last_requested" data={{ date: formatDate(duplicate.lastRequestedAt) }} />}
                            />
                        </ListItem>
                    ))}
                </List>
            </div>
        </Modal>
    );
};

export const DuplicateRequestModalLocalized = withLocalize(DuplicateRequestModal);

export default DuplicateRequestModalLocalized;
